import { readMateSpritePage } from "@/apis/script-game";
import { MateSprite, MateSpriteMode } from "@/types/game";
import { GridEngine, GridEngineConfig, MoveToResult } from "grid-engine";
import { Scene } from "phaser";
import Awaitloader from "phaser3-rex-plugins/plugins/awaitloader.js";
import TiledMap from "tiled-types";
import { useGameUI } from "../store/gameUI";
import { useHouseScene } from "../store/houseScene";
import { useHouseSession } from "../store/houseSession";
import {
  IMateCharacter,
  PlayerCharacterUtils,
  getGEDirectionToSpriteDirection,
  loadImageFromSperateFrames,
} from "../utils/character-utils";
import { EventBus } from "../utils/EventBus";
import { MAP_CONFIGS, MapType, useMap } from "../utils/useMap";
import { useMyHouseMap } from "../utils/useMyHouseMap";
import { useGlobalUser } from "@/stores/globalUser";
import { showConfirmDialog, showFailToast } from "vant";

export class BaseHouseScene extends Scene {
  roomJson?: TiledMap | MyHouseData;
  // @ts-expect-error Autowrided
  gridEngine: GridEngine;

  gameUIStore = useGameUI();
  houseSessionStore = useHouseSession();
  houseSceneStore = useHouseScene();
  map?: Phaser.Tilemaps.Tilemap;
  debugSprite?: MateSprite;
  tileLayers: Phaser.Tilemaps.TilemapLayer[] = [];
  mapFactory!: ReturnType<typeof useMap> | ReturnType<typeof useMyHouseMap>;
  coodinateOffset: { x: number; y: number } = { x: 0, y: 0 };

  constructor(config?: string | Phaser.Types.Scenes.SettingsConfig) {
    super(config);
  }

  preload() {
    this.load.setPath("/game/");
    try {
      this.mapFactory.load();
    } catch (error) {
      this.mapFallback(error as Error);
    }
    const enableDebugSprite = Number(import.meta.env.VITE_HOSUE_PLAYER_DEBUG);
    if (enableDebugSprite) {
      const cb = (
        successCallback: () => void,
        failureCallback: (err?: unknown) => void
      ) => {
        void failureCallback;
        readMateSpritePage({
          pagination: {
            page: 1,
            perPage: 999,
          },
        }).then((res) => {
          const { page } = res;
          const mateSprite = page.data.find(
            (s: MateSprite) => s.animationzz?.length ?? 0 > 0
          );
          if (!mateSprite) return;
          loadImageFromSperateFrames(this, mateSprite);
          this.debugSprite = mateSprite;
          successCallback();
        });
      };
      // @ts-expect-error typing parse error for LSP
      Awaitloader.call(this.load, cb, this);
    }
  }

  init() {
    // 确保在场景初始化时清理之前的监听器
    EventBus.off("resize", this.handleResize, this);

    // 基于最新的 store 数据重新初始化地图资源（支持 scene.restart() 动态切图）
    this.roomJson = this.houseSceneStore.mapJson!;
    if (this.houseSceneStore.currentMapType === MapType.MY_HOUSE) {
      this.mapFactory = useMyHouseMap(this, this.roomJson as MyHouseData);
    } else {
      this.mapFactory = useMap(
        this,
        this.houseSceneStore.currentMapType,
        this.roomJson as TiledMap
      );
    }
  }

  create() {
    // 创建地图
    try {
      const { map, tileLayers, coordinateOffset } = this.mapFactory.create();
      if (coordinateOffset) {
        this.coodinateOffset = coordinateOffset;
      }
      this.tileLayers = tileLayers;
      this.map = map;
    } catch (error) {
      this.mapFallback(error as Error);
    }

    const mapScale = this.mapFactory.mapScale;

    const mapWidth = this.map!.widthInPixels * mapScale;
    const mapHeight = this.map!.heightInPixels * mapScale;
    this.physics.world.setBounds(
      0 - 100,
      0 - 100,
      mapWidth + 200,
      mapHeight + 200
    );

    // 监听尺寸变化事件
    EventBus.on("resize", this.handleResize, this);
    EventBus.emit("current-scene-ready", this);
    // 设置相机
    this.setupMainCamera();

    // GridEngine
    this.gridEngine.create(this.map!, {
      characters: [],
    } as GridEngineConfig);

    // 清理
    this.events.on("destroy", () => {
      this.destroy();
    });

    if (this.debugSprite) {
      const config = MAP_CONFIGS[this.houseSceneStore.currentMapType];
      const playerChar = PlayerCharacterUtils.provideDebugCharacter({
        scene: this,
        gridEngine: this.gridEngine,
        mateSprite: this.debugSprite,
        x: config.startPoint[0],
        y: config.startPoint[1],
        options: {
          scale:
            this.houseSceneStore.currentMapType === MapType.MY_HOUSE ? 0.75 : 1,
        },
      });
      this.cameras.main.startFollow(playerChar.container);
      // 打印位置
      this.gridEngine.positionChangeFinished().subscribe(({ enterTile }) => {
        console.log(`Player Cell Position: ${enterTile.x}, ${enterTile.y}`);
        console.log(`Player Depth: ${playerChar.container.depth}`);
        console.log(
          `Player Coordinate: ${playerChar.container.x}, ${playerChar.container.y}`
        );
      });
    }
  }

  update(): void {
    PlayerCharacterUtils.supplyControl(
      this,
      this.gridEngine,
      this.debugSprite!
    );
  }

  destroy() {
    EventBus.off("resize", this.handleResize, this);
  }

  // 处理尺寸变化
  handleResize() {
    // 重新设置摄像机
    this.setupMainCamera();
  }

  setupMainCamera() {
    const { width: mapWidth, height: mapHeight } = this._getMapSize();
    this.cameras.main.setRoundPixels(true);
    this.cameras.main.setBounds(-300, -300, mapWidth + 600, mapHeight + 600);
  }

  _getMapSize(): { width: number; height: number } {
    return {
      width: this.map!.widthInPixels * this.mapFactory.mapScale,
      height: this.map!.heightInPixels * this.mapFactory.mapScale,
    };
  }

  zoomIn() {
    this.cameras.main.zoomTo(
      Math.min(this.cameras.main.zoom + 0.5, 5),
      500,
      "Linear"
    );
  }
  zoomOut() {
    this.cameras.main.zoomTo(
      Math.max(this.cameras.main.zoom - 0.5, 0.5),
      500,
      "Linear"
    );
  }

  resetZoom() {
    this.cameras.main.stopFollow();
    this.cameras.main.zoomTo(0.5, 1000, "Linear");
  }

  supplyAnimation() {
    this.gridEngine.movementStarted().subscribe(({ charId, direction }) => {
      this.gridEngine
        .getSprite(charId)!
        .anims.play(
          `${charId}_${getGEDirectionToSpriteDirection(direction)}`,
          true
        );
    });
    this.gridEngine.movementStopped().subscribe(({ charId, direction }) => {
      this.gridEngine
        .getSprite(charId)!
        .anims.play(
          `${charId}_${getGEDirectionToSpriteDirection(direction)}_idle`,
          false
        );
    });
  }

  startCameraFollow(char: IMateCharacter, fullScreenOffset: boolean = false) {
    const offsetY = fullScreenOffset ? 100 : 0;
    this.cameras.main.startFollow(
      char.container,
      true,
      1,
      1,
      0 - (char.mateSprite.mode === MateSpriteMode.Compose ? 36 : 24),
      0 - (char.mateSprite.mode === MateSpriteMode.Compose ? 64 : 48) - offsetY
    );
  }

  async moveToAndWait(charId: string, target: { x: number; y: number }) {
    return new Promise<void>((resolve) => {
      const moveTo = this.gridEngine.moveTo(charId, target);
      moveTo.subscribe((result) => {
        if (result.result === MoveToResult.SUCCESS) {
          resolve();
        }
      });
    });
  }

  // 当houseSceneStore
  async mapFallback(error: Error) {
    const isCustomHouse =
      this.houseSceneStore.currentMapType === MapType.MY_HOUSE;
    if (!isCustomHouse) {
      return;
    }
    const userStore = useGlobalUser();
    await userStore.updateUserApi({
      defaultHouseMapId: null,
    });
    showConfirmDialog({
      title: "装修方案加载失败",
      message:
        "已经切换回默认小屋, 方案id: " + userStore.user.defaultHouseMapId,
      confirmButtonText: "确定",
    }).then(() => {
      throw error;
    });
    throw error;
  }
}
